import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Select, useLocalizeMessage } from 'libs.nucleus';
import { FieldPicker } from './field_picker.component';
export const BarDataSection = ({ xAxis, yAxis, splitBy, onXAxisChange, onYAxisChange, onSplitByChange, availableDimensions, availableMeasures, xAxisOptions, exclusiveFieldKey, showSplitBy, splitByRequired, isEditable, }) => {
    const translate = useLocalizeMessage();
    const resolvedXAxisOptions = xAxisOptions ?? availableDimensions;
    // Mutually exclude X, Y, and the optional exclusive field (used for the
    // scatter/bubble Dot area size selection) when they share a pool. For
    // dimension-X chart types the X and Y option pools never overlap, so the
    // filter is a harmless no-op.
    const xAxisFilteredOptions = useMemo(() => {
        const excluded = new Set();
        if (yAxis?.key) {
            excluded.add(yAxis.key);
        }
        if (exclusiveFieldKey) {
            excluded.add(exclusiveFieldKey);
        }
        return excluded.size > 0
            ? resolvedXAxisOptions.filter((option) => !excluded.has(option.key))
            : resolvedXAxisOptions;
    }, [resolvedXAxisOptions, yAxis?.key, exclusiveFieldKey]);
    const yAxisFilteredOptions = useMemo(() => {
        const excluded = new Set();
        if (xAxis?.key) {
            excluded.add(xAxis.key);
        }
        if (exclusiveFieldKey) {
            excluded.add(exclusiveFieldKey);
        }
        return excluded.size > 0 ? availableMeasures.filter((option) => !excluded.has(option.key)) : availableMeasures;
    }, [availableMeasures, xAxis?.key, exclusiveFieldKey]);
    const splitByOptions = useMemo(() => [
        { label: translate('(None)'), value: '' },
        ...availableDimensions.map((field) => ({ label: field.name, value: field.key })),
    ], [availableDimensions, translate]);
    const handleXAxisChange = useCallback((field) => {
        const matchingField = resolvedXAxisOptions.find((option) => option.key === field.key);
        onXAxisChange(matchingField ? { ...matchingField } : { key: field.key, name: field.name, type: field.type });
    }, [resolvedXAxisOptions, onXAxisChange]);
    const handleYAxisChange = useCallback((field) => {
        const matchingField = availableMeasures.find((option) => option.key === field.key);
        onYAxisChange(matchingField ? { ...matchingField } : { key: field.key, name: field.name, type: field.type });
    }, [availableMeasures, onYAxisChange]);
    const handleSplitByChange = useCallback((option) => {
        const matchingField = availableDimensions.find((field) => field.key === option.value);
        onSplitByChange(matchingField ? { ...matchingField } : undefined);
    }, [availableDimensions, onSplitByChange]);
    return (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(FieldPicker, { label: translate('X axis'), required: true, value: xAxis, options: xAxisFilteredOptions, onChange: handleXAxisChange, disabled: !isEditable }), _jsx(FieldPicker, { label: translate('Y axis'), required: true, value: yAxis, options: yAxisFilteredOptions, onChange: handleYAxisChange, disabled: !isEditable }), showSplitBy && (_jsx(Select, { label: translate('Split by'), required: splitByRequired, options: splitByOptions, value: splitByOptions.find((o) => o.value === (splitBy?.key || '')), onChange: handleSplitByChange, width: 'full', disabled: !isEditable, searchableOptions: true }))] }));
};
